// src/components/admin/BooksWithUsers.jsx

import React, { useEffect, useState } from "react";
import { HiRefresh, HiInformationCircle, HiEye, HiSearch, HiX } from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import OrderManager from "../../services/dashboard/OrderManager";

export default function BooksWithUsers() {
  const [withMeOrders, setWithMeOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Fetch all "BOOK_WITH_ME" orders on mount (and on refresh):
  const fetchWithMeOrders = async () => {
    setLoading(true);
    try {
      const orders = await OrderManager.getBooksWithUsers();
      setWithMeOrders(Array.isArray(orders) ? orders : []);
    } catch (err) {
      console.error("Failed to fetch books-with-me:", err);
      setWithMeOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithMeOrders();
  }, []);

  // Get unique customers for filter
  const uniqueCustomers = React.useMemo(() => {
    const customers = withMeOrders.map(order => ({
      id: order.user.userId,
      name: `${order.user.firstName} ${order.user.lastName}`
    }));
    // Remove duplicates
    const unique = customers.filter((customer, index, self) =>
      index === self.findIndex(c => c.id === customer.id)
    );
    return unique.sort((a, b) => a.name.localeCompare(b.name));
  }, [withMeOrders]);

  // Filter orders based on search term, customer filter and sort by latest first
  const filteredOrders = withMeOrders
    .filter(order => {
      // Customer filter
      if (selectedCustomer !== "all" && order.user.userId !== parseInt(selectedCustomer)) {
        return false;
      }
      // Search filter
      if (searchTerm) {
        return (
          order.book.bookName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          `${order.user.firstName} ${order.user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.orderId.toString().includes(searchTerm)
        );
      }
      return true;
    })
    .sort((a, b) => {
      // Sort by creation date - latest first
      const dateA = new Date(a.creationDate[0], a.creationDate[1] - 1, a.creationDate[2], 
                            a.creationDate[3] || 0, a.creationDate[4] || 0, a.creationDate[5] || 0);
      const dateB = new Date(b.creationDate[0], b.creationDate[1] - 1, b.creationDate[2], 
                            b.creationDate[3] || 0, b.creationDate[4] || 0, b.creationDate[5] || 0);
      return dateB - dateA; // Descending order (latest first)
    });

  // Check if any filter is active
  const hasActiveFilters = selectedCustomer !== "all";

  // Clear all filters
  const clearFilters = () => {
    setSelectedCustomer("all");
    setSearchTerm("");
  };

  // Generate user initials for avatar
  const getUserInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Calculate days ago
  const getDaysAgo = (creationDate) => {
    const [year, month, day] = creationDate;
    const orderDate = new Date(year, month - 1, day);
    const today = new Date();
    const diffTime = Math.abs(today - orderDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Calculate duration for view details
  const calculateDuration = (creationDate, returnDate) => {
    const [startYear, startMonth, startDay] = creationDate;
    const startDateObj = new Date(startYear, startMonth - 1, startDay);
    
    let endDateObj;
    if (returnDate) {
      const [endYear, endMonth, endDay] = returnDate;
      endDateObj = new Date(endYear, endMonth - 1, endDay);
    } else {
      endDateObj = new Date(); // Current date if not returned
    }
    
    const diffTime = Math.abs(endDateObj - startDateObj);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-5xl mx-auto">
        {/* Header - Light/Dark Compatible */}
        <header className="flex items-start justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Books with Users
            </h1>
            <p className="text-gray-600 dark:text-slate-400 mt-1.5 text-sm">
              Live view of issued books currently with readers
            </p>
          </div>
          <motion.button
            onClick={fetchWithMeOrders}
            whileHover={{ rotate: 180 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="p-3 rounded-xl bg-white dark:bg-slate-900/50 hover:bg-gray-50 dark:hover:bg-slate-800/70 transition-all duration-200 group shadow-lg hover:shadow-xl border border-gray-200 dark:border-slate-700/50 hover:border-orange-500/30 backdrop-blur-sm"
            title="Refresh"
            aria-label="Refresh list"
          >
            <HiRefresh className="w-5 h-5 text-gray-600 dark:text-slate-400 group-hover:text-orange-500 dark:group-hover:text-orange-400 transition-colors" />
          </motion.button>
        </header>

        {/* Info Banner - Light/Dark Compatible */}
        {/* <div className="flex items-center gap-3 mb-6 p-4 bg-blue-50 dark:bg-slate-900/60 border border-blue-200 dark:border-slate-700/50 rounded-2xl shadow-lg backdrop-blur-sm">
          <HiInformationCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 drop-shadow-sm" />
          <p className="text-sm text-blue-800 dark:text-slate-300 font-medium">
            Showing all orders that have reached "Book with me" status
          </p>
        </div> */}

        {/* Search Bar and Customer Filter - Side by Side */}
        <div className="mb-7 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search Bar - Left Half */}
          <div className="relative">
            <HiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search by book, user, or order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-2.5 bg-white dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700/50 rounded-lg focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500/50 transition-all duration-200 text-sm shadow-sm hover:shadow-xl focus:shadow-2xl backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-slate-500 focus:bg-white dark:focus:bg-slate-900/80"
            />
          </div>

          {/* Customer Filter - Right Half */}
          <div className="relative">
            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              className="w-full px-4 py-2.5 bg-white dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700/50 rounded-lg focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500/50 transition-all duration-200 text-sm shadow-sm hover:shadow-xl focus:shadow-2xl backdrop-blur-sm text-gray-900 dark:text-white focus:bg-white dark:focus:bg-slate-900/80 appearance-none cursor-pointer"
              style={{ 
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 1rem center',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="all">All Customers</option>
              {uniqueCustomers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Filter Summary - Show only when filter is active */}
        {hasActiveFilters && (
          <div className="mb-5 flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/50 rounded-xl px-4 py-3 shadow-lg">
            <p className="text-sm text-green-800 dark:text-green-300 font-medium">
              Showing <span className="font-bold">{filteredOrders.length}</span> of <span className="font-bold">{withMeOrders.length}</span> orders
            </p>
            <button
              onClick={clearFilters}
              className="text-xs font-medium text-green-700 dark:text-green-400 hover:text-green-900 dark:hover:text-green-200 underline transition-colors"
            >
              Clear Filter
            </button>
          </div>
        )}

        {/* Content - Light/Dark Compatible Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-28">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            <span className="ml-3 text-gray-600 dark:text-slate-300 font-medium">Loading...</span>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-28">
            <div className="text-gray-500 dark:text-slate-400 text-lg mb-2 font-medium">
              {searchTerm ? "No matching results found" : "No books are currently with users"}
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 text-sm font-medium transition-colors"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredOrders.map((order, index) => {
              const [year, month, day, hour = 0, minute = 0] = order.creationDate;
              const dateTime = new Date(year, month - 1, day, hour, minute);
              const formattedDate = dateTime.toLocaleDateString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
              });
              const formattedTime = dateTime.toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              });
              const daysAgo = getDaysAgo(order.creationDate);

              return (
                <motion.div
                  key={order.orderId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="group bg-white dark:bg-slate-900/70 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-slate-700/50 p-4 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 hover:border-orange-500/30 hover:bg-gray-50 dark:hover:bg-slate-900/90 shadow-lg dark:shadow-xl hover:shadow-orange-500/5"
                >
                  <div className="flex flex-col gap-3">
                    {/* Top Section - Order ID + Status */}
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-mono font-bold bg-gray-100 dark:bg-slate-800/60 text-gray-700 dark:text-slate-300 shadow-lg border border-gray-200 dark:border-slate-600/30 backdrop-blur-sm">
                        #{order.orderId}
                      </span>
                      <motion.span 
                        whileHover={{ scale: 1.02 }}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 shadow-lg border border-emerald-200 dark:border-emerald-700/50 group-hover:shadow-emerald-500/20 group-hover:bg-emerald-200 dark:group-hover:bg-emerald-900/80 transition-all duration-200 backdrop-blur-sm"
                      >
                        <span className="w-1.5 h-1.5 bg-emerald-500 dark:bg-emerald-400 rounded-full mr-1.5 animate-pulse"></span>
                        Active
                      </motion.span>
                    </div>

                    {/* Book Info */}
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate mb-1 group-hover:text-orange-600 dark:group-hover:text-orange-300 transition-colors duration-200 drop-shadow-sm" title={order.book.bookName}>
                        {order.book.bookName}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-slate-400 font-medium">
                        Issued book • Currently reading
                      </p>
                    </div>

                    {/* User + Date Section */}
                    <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-200 dark:border-slate-700/50">
                      {/* User Info */}
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="relative group/avatar flex-shrink-0">
                          <motion.div 
                            whileHover={{ scale: 1.05 }}
                            className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 via-pink-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shadow-xl ring-2 ring-gray-200 dark:ring-slate-700/50 group-hover:ring-orange-500/30 transition-all duration-300"
                          >
                            {getUserInitials(order.user.firstName, order.user.lastName)}
                          </motion.div>
                          <div className="absolute -inset-2 rounded-full bg-gradient-to-br from-orange-500/20 to-purple-600/20 opacity-0 group-hover/avatar:opacity-100 transition-opacity duration-300 blur-lg -z-10"></div>
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 dark:text-white text-xs truncate drop-shadow-sm">
                            {order.user.firstName} {order.user.lastName}
                          </div>
                        </div>
                      </div>

                      {/* Date Info */}
                      <div className="text-right flex-shrink-0">
                        <div className="font-bold text-gray-900 dark:text-white text-xs mb-0.5 drop-shadow-sm">
                          {formattedDate}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-slate-400 space-x-1">
                          <span className="font-medium">{formattedTime}</span>
                        </div>
                      </div>
                    </div>

                    {/* Days Ago Info */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-slate-700/50">
                      <div className="text-xs text-gray-500 dark:text-slate-400">
                        {daysAgo} days with customer
                      </div>
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        <HiEye className="w-3.5 h-3.5" />
                        View Details
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* View Details Modal */}
        <AnimatePresence>
          {selectedOrder && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedOrder(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between z-10">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Order Details
                  </h2>
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <HiX className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6 space-y-6">
                  {/* Order ID and Status */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">Order ID</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">#{selectedOrder.orderId}</p>
                    </div>
                    <span className="inline-flex items-center px-2 py-2 rounded-lg text-sm font-semibold bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 shadow-lg border border-emerald-200 dark:border-emerald-700/50">
                      <span className="w-2 h-2 bg-emerald-500 dark:bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
                      Currently with Customer
                    </span>
                  </div>

                  {/* Book Details */}
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-slate-800 dark:to-slate-800/50 rounded-xl p-5 border border-orange-100 dark:border-slate-700">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      Book Information
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-gray-600 dark:text-slate-400">Book Title</p>
                        <p className="text-base font-bold text-gray-900 dark:text-white">{selectedOrder.book.bookName}</p>
                      </div>
                      {selectedOrder.book.author && (
                        <div>
                          <p className="text-xs text-gray-600 dark:text-slate-400">Author</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedOrder.book.author.authorName}</p>
                        </div>
                      )}
                      {selectedOrder.book.category && (
                        <div>
                          <p className="text-xs text-gray-600 dark:text-slate-400">Category</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedOrder.book.category.categoryName}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Customer Details */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-800/50 rounded-xl p-5 border border-blue-100 dark:border-slate-700">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Customer Information
                    </h3>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 via-pink-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold shadow-xl">
                        {getUserInitials(selectedOrder.user.firstName, selectedOrder.user.lastName)}
                      </div>
                      <div>
                        <p className="text-base font-bold text-gray-900 dark:text-white">
                          {selectedOrder.user.firstName} {selectedOrder.user.lastName}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-slate-400">{selectedOrder.user.emailId}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-600 dark:text-slate-400">Mobile</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedOrder.user.mobileNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-slate-400">User ID</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">#{selectedOrder.user.userId}</p>
                      </div>
                    </div>
                  </div>

                  {/* Duration Summary */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-slate-800 dark:to-slate-800/50 rounded-xl p-5 border border-purple-100 dark:border-slate-700">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Duration Summary
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white dark:bg-slate-900/50 rounded-lg p-3">
                        <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">Issue Date</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                          {new Date(
                            selectedOrder.creationDate[0],
                            selectedOrder.creationDate[1] - 1,
                            selectedOrder.creationDate[2]
                          ).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </p>
                      </div>
                      <div className="bg-white dark:bg-slate-900/50 rounded-lg p-3">
                        <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">Expected Return</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                          {selectedOrder.expectedReturnDate 
                            ? new Date(
                                selectedOrder.expectedReturnDate[0],
                                selectedOrder.expectedReturnDate[1] - 1,
                                selectedOrder.expectedReturnDate[2]
                              ).toLocaleDateString("en-IN", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : 'N/A'}
                        </p>
                      </div>
                      <div className="col-span-2 bg-gradient-to-r from-orange-500 to-pink-500 rounded-lg p-4 text-white">
                        <p className="text-xs opacity-90 mb-1">Total Days with Customer</p>
                        <p className="text-3xl font-bold">{calculateDuration(selectedOrder.creationDate, selectedOrder.actualReturnDate)} days</p>
                      </div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">Order Status</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{selectedOrder.orderStatus}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">Delivery Status</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{selectedOrder.deliveryStatus || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">Deposit Amount</p>
                        <p className="font-semibold text-gray-900 dark:text-white">₹{selectedOrder.depositAmount || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">Rent Amount</p>
                        <p className="font-semibold text-gray-900 dark:text-white">₹{selectedOrder.rentAmount || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                {/* <div className="sticky bottom-0 bg-gray-50 dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 px-6 py-4">
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="w-full px-4 py-2.5 bg-gray-900 dark:bg-orange-500 hover:bg-gray-800 dark:hover:bg-orange-600 text-white rounded-xl font-medium transition-colors"
                  >
                    Close
                  </button>
                </div> */}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}